import React, { useRef, useState, useCallback, useEffect } from 'react';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { FiCamera, FiVideo, FiVideoOff, FiMic, FiMicOff, FiPhoneOff, FiUpload, FiLoader } from 'react-icons/fi';
import api from '../../services/api';
import { getAbsoluteUrl } from '../../utils/url';
import ImageLightbox from '../ImageLightbox';
import './VideoCallComponent.css';

interface VideoCallComponentProps {
    encargoId: number;
    capturedImages?: string[];
    onPhotoCaptured: (url: string) => void;
    onCallEnded: () => void;
}

interface ParticipantVideoProps {
    participant: any;
    isLocal: boolean;
    videoTrack: MediaStreamTrack | null;
}

const ParticipantVideo: React.FC<ParticipantVideoProps> = ({ participant, isLocal, videoTrack }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    const audioTrack = participant.tracks?.audio?.persistentTrack || participant.tracks?.audio?.track;

    const isVideoOff = participant.tracks?.video?.state === 'off' || !videoTrack;
    const isAudioOff = participant.tracks?.audio?.state === 'off' || !audioTrack;

    useEffect(() => {
        if (videoRef.current) {
            if (videoTrack) {
                videoRef.current.srcObject = new MediaStream([videoTrack]);
            } else {
                videoRef.current.srcObject = null;
            }
        }
    }, [videoTrack]);

    useEffect(() => {
        if (audioRef.current) {
            if (audioTrack && !isLocal) {
                audioRef.current.srcObject = new MediaStream([audioTrack]);
            } else {
                audioRef.current.srcObject = null;
            }
        }
    }, [audioTrack, isLocal]);

    if (isVideoOff) {
        const name = participant.user_name || (isLocal ? 'Tú' : 'Cliente');
        const initials = name.substring(0, 2).toUpperCase();
        return (
            <div className="video-placeholder">
                <div className="video-placeholder-initials">{initials}</div>
                <div className="video-placeholder-text">
                    {isLocal ? 'Tu cámara está apagada' : 'Cámara del cliente apagada'}
                </div>
                {!isLocal && audioTrack && <audio ref={audioRef} autoPlay />}
            </div>
        );
    }

    return (
        <div className="w-full h-full relative">
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={isLocal}
                className="w-full h-full object-cover"
            />
            {!isLocal && audioTrack && <audio ref={audioRef} autoPlay />}
            
            <div className="participant-overlay-name">
                {isLocal ? 'Tú' : (participant.user_name || 'Cliente')}
                {isAudioOff && ' 🔇'}
            </div>
        </div>
    );
};

const VideoCallComponent: React.FC<VideoCallComponentProps> = ({ encargoId, capturedImages, onPhotoCaptured, onCallEnded }) => {
    const [callObject, setCallObject] = useState<DailyCall | null>(null);
    const [callState, setCallState] = useState<'idle' | 'joining' | 'joined' | 'error'>('idle');
    const [roomUrl, setRoomUrl] = useState<string>('');
    const [clientLocation, setClientLocation] = useState<{lat: number, lon: number} | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [participants, setParticipants] = useState<Record<string, any>>({});
    const [participantTracks, setParticipantTracks] = useState<Record<string, MediaStreamTrack>>({});

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    const prvHandleUploadFromComputerClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const prvHandleLocalPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const files = Array.from(e.target.files);
        setUploadingPhotos(true);

        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const res = await api.post(`/video/${encargoId}/foto`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (res.data?.url) {
                    onPhotoCaptured(res.data.url);
                }
            }
        } catch (err) {
            console.error('Error al subir fotos desde el ordenador:', err);
            alert('Error al subir una o más fotos. Inténtalo de nuevo.');
        } finally {
            setUploadingPhotos(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const prvStartCall = async () => {
        try {
            setCallState('joining');
            const res = await api.post(`/video/crear-sala?informe_id=${encargoId}`);
            const url = res.data.url;
            setRoomUrl(url);

            const co = DailyIframe.createCallObject();

            // Obtener también la ubicación local del navegador como respaldo de seguridad
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        setClientLocation(prev => prev || { lat: pos.coords.latitude, lon: pos.coords.longitude });
                    },
                    (err) => console.warn("Geo local fallback:", err),
                    { enableHighAccuracy: true }
                );
            }

            co.on('joined-meeting', () => {
                setCallState('joined');
                setParticipants(co.participants());
                // Solicitar ubicación GPS a los participantes en la sala
                co.sendAppMessage({ type: 'request_gps' }, '*');
                co.sendAppMessage(JSON.stringify({ type: 'request_gps' }), '*');
            });

            co.on('left-meeting', () => {
                setCallState('idle');
                onCallEnded();
                co.destroy();
                setCallObject(null);
                setParticipants({});
                setParticipantTracks({});
            });

            co.on('error', () => setCallState('error'));
            co.on('recording-started', () => setIsRecording(true));
            co.on('recording-stopped', () => setIsRecording(false));
            co.on('app-message', (event: any) => {
                let payload = event?.data;
                if (typeof payload === 'string') {
                    try { payload = JSON.parse(payload); } catch (e) {}
                }
                if (payload && (payload.type === 'gps_update' || payload.lat) && payload.lat && payload.lon) {
                    setClientLocation({ lat: Number(payload.lat), lon: Number(payload.lon) });
                }
            });

            const prvHandleParticipantsChange = () => {
                setParticipants(co.participants());
            };

            co.on('participant-joined', () => {
                prvHandleParticipantsChange();
                // Solicitar ubicación al nuevo participante
                co.sendAppMessage({ type: 'request_gps' }, '*');
                co.sendAppMessage(JSON.stringify({ type: 'request_gps' }), '*');
            });
            co.on('participant-updated', prvHandleParticipantsChange);
            
            co.on('participant-left', (event: any) => {
                prvHandleParticipantsChange();
                if (event.participant) {
                    const pid = event.participant.local ? 'local' : event.participant.session_id;
                    if (pid) {
                        setParticipantTracks(prev => {
                            const next = { ...prev };
                            delete next[pid];
                            return next;
                        });
                    }
                }
            });

            co.on('track-started', (event: any) => {
                prvHandleParticipantsChange();
                if (event.participant && event.track && event.track.kind === 'video') {
                    const pid = event.participant.local ? 'local' : event.participant.session_id;
                    if (pid) {
                        setParticipantTracks(prev => ({
                            ...prev,
                            [pid]: event.track
                        }));
                    }
                }
            });

            co.on('track-stopped', (event: any) => {
                prvHandleParticipantsChange();
                if (event.participant && event.track && event.track.kind === 'video') {
                    const pid = event.participant.local ? 'local' : event.participant.session_id;
                    if (pid) {
                        setParticipantTracks(prev => {
                            const next = { ...prev };
                            delete next[pid];
                            return next;
                        });
                    }
                }
            });

            await co.join({ url });
            setCallObject(co);

        } catch (error) {
            console.error("Error starting call:", error);
            setCallState('error');
        }
    };

    const prvLeaveCall = useCallback(() => {
        if (callObject) {
            callObject.leave();
        }
    }, [callObject]);

    const prvCapturePhoto = async () => {
        if (!callObject) return;
        
        try {
            const pts = callObject.participants();
            const remoteParticipant = Object.values(pts).find(p => !p.local);
            const localParticipant = pts.local;

            const remotePid = remoteParticipant?.session_id;
            let targetTrack = (remotePid && participantTracks[remotePid]) || remoteParticipant?.tracks?.video?.persistentTrack || remoteParticipant?.tracks?.video?.track;

            if (!targetTrack && localParticipant?.tracks?.video?.persistentTrack) {
                targetTrack = participantTracks['local'] || localParticipant.tracks.video.persistentTrack || localParticipant.tracks.video.track;
                alert("Aviso (Modo Pruebas): Capturando tu propia cámara local porque no hay ningún cliente remoto con video activo en la sala.");
            }

            if (!targetTrack) {
                const info = Object.keys(pts).map(id => {
                    const p = pts[id];
                    const state = p.tracks?.video?.state || 'sin_estado';
                    return `${id === 'local' ? 'tú (local)' : id} (video: ${p.video ? 'sí' : 'no'}, estado: ${state})`;
                }).join(", ");
                alert(`No hay ningún participante con video activo para capturar.\nDetalle: ${info}`);
                return;
            }

            let lat: number | null = clientLocation?.lat || null;
            let lon: number | null = clientLocation?.lon || null;
            if (lat === null || lon === null) {
                console.warn("No se recibió la ubicación del cliente todavía.");
            }

            let blob: Blob;

            try {
                // Intentar capturar directamente del elemento <video> activo en el DOM si ya está reproduciendo
                const activeVideoElements = Array.from(document.querySelectorAll('.custom-video-area video, video')) as HTMLVideoElement[];
                const matchingActiveVideo = activeVideoElements.find(v => v.readyState >= 2 && v.videoWidth > 0 && !v.paused);

                let sourceElement: CanvasImageSource;
                let width: number;
                let height: number;
                let tempVideoEl: HTMLVideoElement | null = null;

                if (matchingActiveVideo) {
                    sourceElement = matchingActiveVideo;
                    width = matchingActiveVideo.videoWidth || 1280;
                    height = matchingActiveVideo.videoHeight || 720;
                } else {
                    tempVideoEl = document.createElement('video');
                    tempVideoEl.srcObject = new MediaStream([targetTrack]);
                    tempVideoEl.autoplay = true;
                    tempVideoEl.playsInline = true;
                    tempVideoEl.muted = true;
                    
                    await new Promise<void>((resolve, reject) => {
                        const checkReady = () => {
                            if (tempVideoEl && tempVideoEl.readyState >= 2 && tempVideoEl.videoWidth > 0) {
                                setTimeout(() => resolve(), 300);
                            } else {
                                setTimeout(checkReady, 50);
                            }
                        };
                        tempVideoEl!.onloadeddata = () => checkReady();
                        tempVideoEl!.oncanplay = () => checkReady();
                        tempVideoEl!.onerror = () => reject(new Error("Error en elemento video auxiliar"));
                        tempVideoEl!.play().then(() => checkReady()).catch(() => setTimeout(resolve, 600));
                        setTimeout(resolve, 2500);
                    });

                    sourceElement = tempVideoEl;
                    const settings = targetTrack.getSettings();
                    width = settings.width || tempVideoEl.videoWidth || 1280;
                    height = settings.height || tempVideoEl.videoHeight || 720;
                }

                const canvasEl = document.createElement('canvas');
                canvasEl.width = width;
                canvasEl.height = height;
                
                const ctx = canvasEl.getContext('2d');
                if (!ctx) throw new Error("Contexto 2D no disponible");
                ctx.drawImage(sourceElement, 0, 0, width, height);

                // Añadir marca de agua
                if (lat !== null && lon !== null) {
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
                    ctx.fillRect(0, height - 70, width, 70);
                    ctx.fillStyle = 'white';
                    ctx.font = '20px sans-serif';
                    const dateStr = new Date().toLocaleString();
                    ctx.fillText(`GPS: ${lat.toFixed(6)}, ${lon.toFixed(6)}`, 20, height - 40);
                    ctx.fillText(`Fecha: ${dateStr}`, 20, height - 15);
                }
                
                const canvasBlob = await new Promise<Blob | null>((resolve) => {
                    canvasEl.toBlob((b) => resolve(b), 'image/jpeg', 0.90);
                });
                
                if (tempVideoEl) {
                    tempVideoEl.pause();
                    tempVideoEl.srcObject = null;
                }

                if (!canvasBlob) throw new Error("Fallo al crear blob de canvas");
                blob = canvasBlob;

            } catch (canvasErr) {
                console.warn("Fallo la captura por Canvas, recurriendo a ImageCapture nativo:", canvasErr);
                if ((window as any).ImageCapture) {
                    const imageCapture = new (window as any).ImageCapture(targetTrack);
                    blob = await imageCapture.takePhoto();
                } else {
                    throw new Error("Navegador incompatible: no soporta Canvas ni ImageCapture para capturar frames.");
                }
            }
            
            const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append('file', file);
            
            let uploadUrl = `/video/${encargoId}/foto`;
            const params = [];
            if (lat !== null) params.push(`lat=${lat}`);
            if (lon !== null) params.push(`lon=${lon}`);
            if (params.length > 0) {
                uploadUrl += `?${params.join('&')}`;
            }

            const res = await api.post(uploadUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data && res.data.url) {
                onPhotoCaptured(res.data.url);
            }
        } catch (err: any) {
            console.error("Error capturing photo:", err);
            alert("No se pudo capturar la foto: " + (err.message || err));
        }
    };

    const prvHandleDeleteImage = async (url: string) => {
        const confirmDelete = window.confirm("¿Estás seguro de que deseas eliminar esta captura?");
        if (!confirmDelete) return;

        try {
            await api.delete(`/video/${encargoId}/foto?foto_url=${encodeURIComponent(url)}`);
            onPhotoCaptured('');
        } catch (err: any) {
            console.error("Error deleting image:", err);
            alert("No se pudo eliminar la foto: " + (err.message || err));
        }
    };

    const prvToggleRecording = () => {
        if (!callObject) return;
        if (isRecording) {
            callObject.stopRecording();
        } else {
            callObject.startRecording();
        }
    };
    
    const getClientUrl = () => {
        if (!roomUrl) return '';
        return `${window.location.origin}/sala-cliente?roomUrl=${encodeURIComponent(roomUrl)}`;
    };

    const prvCopyDirectUrl = () => {
        navigator.clipboard.writeText(roomUrl);
        alert("URL directa de la videoconferencia (Daily.co) copiada al portapapeles.");
    };

    const prvCopyClientUrl = () => {
        navigator.clipboard.writeText(getClientUrl());
        alert("Enlace web del cliente (con capturador GPS) copiado al portapapeles.");
    };

    const remoteParticipant = Object.values(participants).find(p => !p.local);
    const localParticipant = participants.local;

    const remotePid = remoteParticipant?.session_id;
    const remoteVideoTrack = (remotePid && participantTracks[remotePid]) || remoteParticipant?.tracks?.video?.persistentTrack || remoteParticipant?.tracks?.video?.track || null;
    const localVideoTrack = participantTracks['local'] || localParticipant?.tracks?.video?.persistentTrack || localParticipant?.tracks?.video?.track || null;

    const isLocalVideoEnabled = localParticipant?.video ?? false;
    const isLocalAudioEnabled = localParticipant?.audio ?? false;

    const prvToggleLocalVideo = () => {
        if (callObject && localParticipant) {
            callObject.setLocalVideo(!isLocalVideoEnabled);
        }
    };

    const prvToggleLocalAudio = () => {
        if (callObject && localParticipant) {
            callObject.setLocalAudio(!isLocalAudioEnabled);
        }
    };

    useEffect(() => {
        return () => {
            if (callObject) {
                callObject.leave().then(() => callObject.destroy());
            }
        };
    }, [callObject]);

    return (
        <div className="video-call-container flex flex-col gap-4">
            {callState === 'idle' ? (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                    <FiVideo className="text-4xl text-blue-500 mb-4" />
                    <h3 className="text-lg font-medium mb-2">Videollamada Integrada</h3>
                    <p className="text-gray-500 text-center mb-6 max-w-md">
                        Inicia una sala de videoperitación. Podrás capturar fotos en tiempo real y grabar la sesión directamente a la nube.
                    </p>
                    <button className="btn btn-primary" onClick={prvStartCall}>
                        Crear e Iniciar Sala
                    </button>
                </div>
            ) : (
                <>
                    {callState === 'joining' && <div className="text-center p-4">Conectando con la sala...</div>}
                    
                    {roomUrl && (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <div className="flex justify-between items-center text-sm">
                                    <div className="overflow-hidden text-ellipsis whitespace-nowrap mr-2">
                                        <span className="font-semibold text-blue-900">URL Directa Videoconferencia (Daily.co): </span>
                                        <a 
                                            href={roomUrl} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            className="select-all text-blue-700 font-mono bg-white px-2 py-1 rounded ml-2 border border-blue-200 hover:underline"
                                        >
                                            {roomUrl}
                                        </a>
                                    </div>
                                    <button 
                                        className="btn text-xs px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap" 
                                        onClick={prvCopyDirectUrl}
                                    >
                                        Copiar URL Directa
                                    </button>
                                </div>

                                <div className="flex justify-between items-center text-sm border-t border-blue-200 pt-2 mt-1">
                                    <div className="overflow-hidden text-ellipsis whitespace-nowrap mr-2">
                                        <span className="font-semibold text-blue-800">Enlace Web Cliente (con GPS): </span>
                                        <span className="select-all text-blue-600 bg-white px-2 py-1 rounded ml-2 border border-blue-200">{getClientUrl()}</span>
                                    </div>
                                    <button 
                                        className="btn text-xs px-3 py-1 bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap" 
                                        onClick={prvCopyClientUrl}
                                    >
                                        Copiar Enlace Cliente (GPS)
                                    </button>
                                </div>
                            </div>

                            {clientLocation ? (
                                <div className="flex items-center gap-2 text-sm bg-green-50 text-green-800 p-2 rounded-lg border border-green-200">
                                    <span className="relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                    </span>
                                    <strong>Ubicación GPS del cliente en vivo:</strong>
                                    <span className="font-mono bg-white px-2 py-0.5 rounded border border-green-300">
                                        {clientLocation.lat.toFixed(6)}, {clientLocation.lon.toFixed(6)}
                                    </span>
                                    <a 
                                        href={`https://www.google.com/maps?q=${clientLocation.lat},${clientLocation.lon}`} 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="ml-auto text-green-700 hover:underline font-medium flex items-center gap-1"
                                    >
                                        Ver en Google Maps
                                    </a>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-sm bg-amber-50 text-amber-800 p-2 rounded-lg border border-amber-200">
                                    <span className="relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                                    </span>
                                    <strong>Ubicación GPS:</strong>
                                    <span className="italic">Esperando señal GPS del cliente (se solicitará al unirse)...</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="custom-video-area relative">
                        {/* Overlay flotante con coordenadas GPS en vivo sobre el reproductor de video */}
                        {clientLocation && (
                            <div className="absolute top-3 left-3 z-10 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-md text-xs font-mono flex items-center gap-2 border border-white/20 shadow-lg">
                                <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
                                <span>GPS: {clientLocation.lat.toFixed(6)}, {clientLocation.lon.toFixed(6)}</span>
                            </div>
                        )}

                        {remoteParticipant ? (
                            <div className="remote-video-container">
                                <ParticipantVideo 
                                    participant={remoteParticipant} 
                                    isLocal={false} 
                                    videoTrack={remoteVideoTrack}
                                />
                            </div>
                        ) : (
                            <div className="waiting-overlay">
                                <div className="pulse-loader"></div>
                                <h4 className="text-md font-medium">Esperando a que el cliente se una...</h4>
                                <p className="text-xs text-muted max-w-xs m-0">
                                    Comparte el enlace superior con el cliente para que pueda ingresar a la videollamada.
                                </p>
                            </div>
                        )}

                        {localParticipant && (
                            <div className="local-pip-container">
                                <ParticipantVideo 
                                    participant={localParticipant} 
                                    isLocal={true} 
                                    videoTrack={localVideoTrack}
                                />
                            </div>
                        )}
                    </div>
                    
                    {callState === 'joined' && (
                        <div className="call-controls-toolbar">
                            <button 
                                className={`control-btn ${isLocalAudioEnabled ? 'active' : 'danger-toggle'}`}
                                onClick={prvToggleLocalAudio}
                                title={isLocalAudioEnabled ? "Silenciar Micrófono" : "Activar Micrófono"}
                            >
                                {isLocalAudioEnabled ? <FiMic /> : <FiMicOff />}
                                {isLocalAudioEnabled ? 'Silenciar' : 'Activar Mic'}
                            </button>

                            <button 
                                className={`control-btn ${isLocalVideoEnabled ? 'active' : 'danger-toggle'}`}
                                onClick={prvToggleLocalVideo}
                                title={isLocalVideoEnabled ? "Apagar Cámara" : "Encender Cámara"}
                            >
                                {isLocalVideoEnabled ? <FiVideo /> : <FiVideoOff />}
                                {isLocalVideoEnabled ? 'Apagar Cámara' : 'Encender Cámara'}
                            </button>

                            <button 
                                className={`control-btn ${isRecording ? 'recording-active' : ''}`}
                                onClick={prvToggleRecording}
                            >
                                <span className={`recording-dot ${isRecording ? 'active' : ''}`}></span>
                                {isRecording ? 'Detener Grabación' : 'Grabar en Nube'}
                            </button>
                            
                            <button 
                                className="control-btn active"
                                onClick={prvCapturePhoto}
                                title="Extrae un frame de la cámara del cliente"
                            >
                                <FiCamera /> Capturar Foto del Cliente
                            </button>
                            
                            <button 
                                className="control-btn danger-toggle"
                                style={{ marginLeft: 'auto' }}
                                onClick={prvLeaveCall}
                            >
                                <FiPhoneOff /> Finalizar
                            </button>
                        </div>
                    )}
                </>
            )}

            <div className="photos-gallery-section">
                <div className="photos-gallery-title flex justify-between items-center w-full">
                    <h4 title="Haz clic en una imagen para abrir el visualizador" style={{ cursor: 'pointer' }}>
                        <FiCamera className="text-blue-500" /> Fotos Capturadas ({capturedImages?.length || 0})
                    </h4>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            accept="image/*" 
                            multiple 
                            style={{ display: 'none' }} 
                            onChange={prvHandleLocalPhotoUpload} 
                        />
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={prvHandleUploadFromComputerClick}
                            disabled={uploadingPhotos}
                            title="Subir fotos desde el ordenador"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                padding: '0',
                                borderRadius: '6px',
                                border: '1px solid #2563eb',
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                cursor: uploadingPhotos ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                fontSize: '18px'
                            }}
                        >
                            {uploadingPhotos ? (
                                <FiLoader className="animate-spin text-blue-600" />
                            ) : (
                                <FiUpload className="text-blue-600" />
                            )}
                        </button>
                    </div>
                </div>
                
                {capturedImages && capturedImages.length > 0 ? (
                    <div className="photos-gallery-list">
                        {capturedImages.map((url, idx) => (
                            <div 
                                key={idx} 
                                className="photo-thumb-item" 
                                title="Haz clic en una imagen para abrir el visualizador"
                            >
                                <img 
                                    src={getAbsoluteUrl(url)} 
                                    alt={`Captura ${idx + 1}`} 
                                    onClick={() => setLightboxIndex(idx)}
                                />
                                <button 
                                    className="photo-delete-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        prvHandleDeleteImage(url);
                                    }}
                                    title="Eliminar captura"
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="photos-gallery-empty">
                        <FiCamera />
                        <p className="font-medium">Aún no se han capturado fotos en esta sesión.</p>
                        <p className="text-[10px] mt-1">Usa el botón "Capturar Foto del Cliente" una vez iniciada la llamada.</p>
                    </div>
                )}
            </div>
            
            {lightboxIndex !== null && capturedImages && (
                <ImageLightbox 
                    images={capturedImages.map(url => getAbsoluteUrl(url))}
                    currentIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onIndexChange={(idx) => setLightboxIndex(idx)}
                />
            )}
        </div>
    );
};

export default VideoCallComponent;
